import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Search,
  Settings,
  User,
  LogOut,
  ChevronDown,
  Menu,
  Download,
  X,
  MessageCircle,
} from "lucide-react";
import CheckInOutButton from "./CheckInOutButton";
import notificationService from "../services/notificationService";
import pushNotificationService from "../services/pushNotificationService";
import { getSidebarModulesForDynamicSidebar } from "../config/sidebarConfig";
import SupportTicketModal from "./SupportTicketModal";
import { useWebSocket } from "../contexts/WebSocketContext";

const Navbar = ({ user, onLogout, isSidebarCollapsed }) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showSupportModal, setShowSupportModal] = useState(false);

  const {
    socket: wsSocket,
    isConnected: wsConnected,
    on: wsOn,
  } = useWebSocket();

  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const searchInputRef = useRef(null);

  // PWA Install prompt handler
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      alert(
        "App installation is not available. Make sure you are using HTTPS and the app meets PWA requirements.",
      );
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      console.log("User accepted the install prompt");
    }

    setDeferredPrompt(null);
  };

  // Fetch notifications from API
  const fetchNotifications = async () => {
    try {
      setIsLoadingNotifications(true);
      const response = await notificationService.getNotifications({
        limit: 10,
      });
      if (response?.data?.notifications) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      if (response?.data?.count !== undefined) {
        const count = response.data.count;
        setUnreadCount(count);

        // Update PWA badge
        await pushNotificationService.setBadgeCount(count);
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  // Initial fetch and polling (polling is a fallback - the socket event below
  // keeps the bell fresh in realtime)
  useEffect(() => {
    if (user) {
      // Fetch immediately on mount
      fetchNotifications();
      fetchUnreadCount();

      // Poll every 30 seconds as a fallback for new notifications
      const notificationInterval = setInterval(() => {
        fetchNotifications();
        fetchUnreadCount();
      }, 30000);

      return () => clearInterval(notificationInterval);
    }
  }, [user]);

  // Realtime notification updates via WebSocket - the bell updates instantly
  // when a new notification is created for this user.
  useEffect(() => {
    if (!user || !wsSocket || !wsConnected) return;
    const unsubscribe = wsOn("notification:new", () => {
      fetchNotifications();
      fetchUnreadCount();
    });
    return unsubscribe;
  }, [user, wsSocket, wsConnected, wsOn]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Load search results from sidebar modules
  useEffect(() => {
    try {
      const modules = getSidebarModulesForDynamicSidebar();
      const items = Object.values(modules).flatMap((module) => [
        {
          label: module.name,
          path: module.path,
          icon: module.icon,
          type: "module",
        },
        ...(module.subItems || []).map((sub) => ({
          label: sub.name,
          path: sub.path,
          icon: sub.icon,
          type: "page",
          parent: module.name,
        })),
      ]);
      setSearchResults(items);
    } catch (error) {
      console.error("Failed to load search data:", error);
      setSearchResults([]);
    }
  }, []);

  // Keyboard shortcut for search (Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setShowSearch(true);
      }
      if (e.key === "Escape" && showSearch) {
        setShowSearch(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showSearch]);

  const markAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      // Update local state
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n,
        ),
      );
      // Refresh unread count
      fetchUnreadCount();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString(),
        })),
      );
      // Refresh unread count
      fetchUnreadCount();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "success":
        return "✅";
      case "warning":
        return "⚠️";
      case "error":
        return "❌";
      case "reminder":
        return "⏰";
      case "info":
      default:
        return "ℹ️";
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return time.toLocaleDateString();
  };

  // (push preference toggles removed — managed via Settings)

  const getUserTypeDisplay = () => {
    if (user?.UserType) {
      return user.UserType.display_name;
    }
    if (user?.user_type_id) {
      return "Custom Access";
    }
    return user?.Role?.display_name || "User";
  };

  const getUserTypeColor = () => {
    if (user?.UserType?.color) {
      const colors = {
        blue: "bg-blue-100 text-blue-800",
        green: "bg-green-100 text-green-800",
        purple: "bg-purple-100 text-purple-800",
        yellow: "bg-yellow-100 text-yellow-800",
        red: "bg-red-100 text-red-800",
        gray: "bg-gray-100 text-gray-800",
      };
      return colors[user.UserType.color] || "bg-blue-100 text-blue-800";
    }
    return "bg-blue-100 text-blue-800";
  };

  // Debug: Log user avatar data
  React.useEffect(() => {
    if (user) {
      console.log("👤 User Avatar Debug:", {
        cdn_avatar_url: user.cdn_avatar_url,
        avatar: user.avatar,
        "Employee.cdn_avatar_url": user.Employee?.cdn_avatar_url,
        "Employee.avatar": user.Employee?.avatar,
        finalAvatar:
          user.cdn_avatar_url ||
          user.avatar ||
          user.Employee?.cdn_avatar_url ||
          user.Employee?.avatar,
      });
    }
  }, [user]);

  return (
    <>
      <header
        className="fixed top-0 right-0 z-40 h-16 bg-white/80 backdrop-blur-md border-b transition-all duration-300"
        style={{
          left: isSidebarCollapsed ? "64px" : "256px",
          width: isSidebarCollapsed
            ? "calc(100% - 64px)"
            : "calc(100% - 256px)",
          borderBottomColor: "rgba(223, 107, 56, 0.08)",
        }}
      >
        <div className="h-full px-6 flex items-center justify-between">
          {/* Left side - Logo & Search */}
          <div className="flex items-center gap-4">
            {/* Puffin Group Logo */}
            <img
              src="/logo.png"
              alt="Puffin Group"
              className="h-8 w-auto object-contain"
            />

            {/* Search */}
            <button
              onClick={() => setShowSearch(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-all"
            >
              <Search className="h-4 w-4" />
              <span className="hidden md:inline text-xs">
                Search modules...
              </span>
              <kbd className="hidden lg:inline-block px-1.5 py-0.5 text-xs text-gray-400 bg-gray-100 border border-gray-200 rounded">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Right side - Actions */}
          {user && (
            <div className="flex items-center gap-1.5">
              {/* Check In/Out Button */}
              <CheckInOutButton />

              {/* Support Button */}
              <button
                onClick={() => setShowSupportModal(true)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
                title="Help & Support"
              >
                <MessageCircle className="h-4 w-4" />
              </button>

              {/* Notifications */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                  }}
                  className="relative w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
                >
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-[#DF6B38] text-white text-[9px] font-bold flex items-center justify-center">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 z-50 overflow-hidden">
                    <div className="p-3 border-b border-gray-100">
                      <div className="flex justify-between items-center">
                        <h3 className="text-xs font-semibold text-gray-900">
                          Notifications
                        </h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-[#DF6B38] hover:text-[#c85f30] font-medium"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {isLoadingNotifications ? (
                        <div className="p-6 text-center">
                          <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-[#DF6B38] border-r-transparent"></div>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="p-8 text-center">
                          <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                          <p className="text-xs text-gray-500">
                            No notifications
                          </p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-all ${
                              !notification.is_read ? "bg-orange-50/20" : ""
                            }`}
                            onClick={() => {
                              markAsRead(notification.id);
                              if (notification.action_url) {
                                let path = notification.action_url;
                                try {
                                  if (
                                    path.startsWith("http://") ||
                                    path.startsWith("https://")
                                  ) {
                                    const url = new URL(path);
                                    path = url.pathname;
                                  }
                                } catch (e) {
                                  console.log("Using action_url as-is:", path);
                                }
                                navigate(path);
                                setShowNotifications(false);
                              }
                            }}
                          >
                            <div className="flex items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p
                                    className={`text-xs text-gray-900 leading-snug ${
                                      !notification.is_read
                                        ? "font-semibold"
                                        : "font-medium"
                                    }`}
                                  >
                                    {notification.title}
                                  </p>
                                  {!notification.is_read && (
                                    <div className="flex-shrink-0 w-1.5 h-1.5 bg-[#DF6B38] rounded-full mt-1"></div>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-1">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {formatTimeAgo(notification.created_at)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="p-2 border-t border-gray-100">
                      <button
                        onClick={() => {
                          navigate("/self-service/notifications");
                          setShowNotifications(false);
                        }}
                        className="w-full text-center text-xs text-[#DF6B38] hover:text-[#c85f30] font-medium py-1.5"
                      >
                        View all
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User Menu */}
              <div className="relative ml-2" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-1.5 py-1 hover:bg-gray-50 rounded-lg transition-all"
                >
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center overflow-hidden"
                    style={{
                      background: "linear-gradient(135deg, #DF6B38, #DF383F)",
                    }}
                  >
                    {user.cdn_avatar_url ||
                    user.avatar ||
                    user.Employee?.cdn_avatar_url ||
                    user.Employee?.avatar ? (
                      <img
                        src={
                          user.cdn_avatar_url ||
                          user.avatar ||
                          user.Employee?.cdn_avatar_url ||
                          user.Employee?.avatar
                        }
                        alt={`${user.first_name} ${user.last_name}`}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <span
                      className="text-xs font-semibold text-white"
                      style={{
                        display:
                          user.cdn_avatar_url ||
                          user.avatar ||
                          user.Employee?.cdn_avatar_url ||
                          user.Employee?.avatar
                            ? "none"
                            : "flex",
                      }}
                    >
                      {user.first_name?.[0]}
                      {user.last_name?.[0]}
                    </span>
                  </div>
                  <div className="hidden lg:block text-left">
                    <p className="text-xs font-medium text-gray-700 leading-tight">
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-xs text-gray-400 leading-tight">
                      {getUserTypeDisplay()}
                    </p>
                  </div>
                  <ChevronDown className="h-3 w-3 text-gray-400" />
                </button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 z-50 overflow-hidden">
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-900">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {user.email}
                      </p>
                    </div>

                    <div className="p-1.5">
                      <button
                        onClick={() => {
                          navigate("/self-service/profile");
                          setShowUserMenu(false);
                        }}
                        className="flex items-center w-full px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 rounded transition-all"
                      >
                        <User className="h-3.5 w-3.5 mr-2" />
                        Profile
                      </button>
                      <button
                        onClick={() => {
                          navigate("/self-service");
                          setShowUserMenu(false);
                        }}
                        className="flex items-center w-full px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 rounded transition-all"
                      >
                        <Settings className="h-3.5 w-3.5 mr-2" />
                        Self Service
                      </button>
                      <button
                        onClick={() => {
                          handleInstallClick();
                          setShowUserMenu(false);
                        }}
                        className="flex items-center w-full px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 rounded transition-all"
                      >
                        <Download className="h-3.5 w-3.5 mr-2" />
                        Install App
                      </button>
                    </div>

                    <div className="border-t border-gray-100 p-1.5">
                      <button
                        onClick={onLogout}
                        className="flex items-center w-full px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-all"
                      >
                        <LogOut className="h-3.5 w-3.5 mr-2" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Search Modal */}
      {showSearch && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-20">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4">
            <div className="flex items-center gap-3 p-4 border-b border-gray-200">
              <Search className="h-5 w-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search modules, pages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 outline-none text-gray-900 placeholder-gray-400"
                autoFocus
              />
              <button
                onClick={() => {
                  setShowSearch(false);
                  setSearchQuery("");
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto p-2">
              {(() => {
                const filtered = searchQuery
                  ? searchResults
                      .filter(
                        (item) =>
                          item.label
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          (item.parent &&
                            item.parent
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase())),
                      )
                      .slice(0, 10)
                  : searchResults.slice(0, 10);

                if (filtered.length === 0 && searchQuery) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">No results found</p>
                    </div>
                  );
                }

                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">Loading...</p>
                    </div>
                  );
                }

                return filtered.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      navigate(item.path);
                      setShowSearch(false);
                      setSearchQuery("");
                    }}
                    className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  >
                    {item.icon && (
                      <item.icon className="h-5 w-5 text-gray-600" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {item.label}
                      </p>
                      {item.parent && (
                        <p className="text-xs text-gray-500">{item.parent}</p>
                      )}
                    </div>
                  </button>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Support Ticket Modal */}
      <SupportTicketModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />
    </>
  );
};

export default Navbar;
